import React, { useState } from 'react';
import { Plus, Search, Trash2, MailX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUnsubscribes, addUnsubscribe, removeUnsubscribe, EmailUnsubscribe } from '@/services/campaignUnsubscribeService';
import { toast } from 'sonner';
import { format } from 'date-fns';

interface Props {
  companyId: string;
}

const CampaignUnsubscribeManager: React.FC<Props> = ({ companyId }) => {
  const { data: unsubscribes = [], isLoading } = useQuery<EmailUnsubscribe[]>({
    queryKey: ['email-unsubscribes', companyId],
    queryFn: () => fetchUnsubscribes(companyId || undefined),
  });
  const qc = useQueryClient();

  const addMutation = useMutation({
    mutationFn: ({ email, reason }: { email: string; reason?: string }) => addUnsubscribe(email, reason, companyId || undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['email-unsubscribes'] }); toast.success('Unsubscribed'); },
    onError: (e: any) => toast.error(e.message),
  });

  const removeMutation = useMutation({
    mutationFn: removeUnsubscribe,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['email-unsubscribes'] }); toast.success('Resubscribed'); },
    onError: (e: any) => toast.error(e.message),
  });

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newReason, setNewReason] = useState('');
  const [removeId, setRemoveId] = useState<string | null>(null);

  const filtered = unsubscribes.filter(u => {
    if (search && !u.email.toLowerCase().includes(search.toLowerCase())) return false;
    if (filter === 'global' && !u.global_unsubscribe) return false;
    if (filter === 'partial' && u.global_unsubscribe) return false;
    return true;
  });

  const totalGlobal = unsubscribes.filter(u => u.global_unsubscribe).length;
  const thisMonth = unsubscribes.filter(u => {
    const d = new Date(u.unsubscribed_at);
    const now = new Date();
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Total</div><div className="text-2xl font-bold">{unsubscribes.length}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">Global</div><div className="text-2xl font-bold">{totalGlobal}</div></CardContent></Card>
        <Card><CardContent className="p-4"><div className="text-sm text-muted-foreground">This Month</div><div className="text-2xl font-bold">{thisMonth}</div></CardContent></Card>
      </div>

      {/* Controls */}
      <div className="flex gap-2 items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="global">Global Only</SelectItem>
            <SelectItem value="partial">Partial Only</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={() => setAddOpen(true)}><Plus className="h-4 w-4 mr-1.5" />Unsubscribe</Button>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="hidden md:table-cell">Reason</TableHead>
              <TableHead className="hidden sm:table-cell">Date</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No unsubscribes found.</TableCell></TableRow>
            ) : filtered.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">{u.email}</TableCell>
                <TableCell>
                  {u.global_unsubscribe ? (
                    <Badge variant="destructive">Global</Badge>
                  ) : (
                    (u.unsubscribed_from || []).map(t => <Badge key={t} variant="secondary" className="mr-1">{t}</Badge>)
                  )}
                </TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">{u.reason || '—'}</TableCell>
                <TableCell className="hidden sm:table-cell text-sm">{format(new Date(u.unsubscribed_at), 'MMM d, yyyy')}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="sm" onClick={() => setRemoveId(u.id)}>Resubscribe</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Add Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manual Unsubscribe</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2"><Label>Email *</Label><Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="email@example.com" /></div>
            <div className="space-y-2"><Label>Reason</Label><Textarea value={newReason} onChange={e => setNewReason(e.target.value)} placeholder="Optional reason..." rows={2} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={() => { addMutation.mutate({ email: newEmail, reason: newReason || undefined }); setAddOpen(false); setNewEmail(''); setNewReason(''); }} disabled={!newEmail}>Unsubscribe</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Resubscribe */}
      <AlertDialog open={!!removeId} onOpenChange={() => setRemoveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Resubscribe?</AlertDialogTitle><AlertDialogDescription>This will remove the unsubscribe record and allow emails to be sent again.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (removeId) { removeMutation.mutate(removeId); setRemoveId(null); } }}>Resubscribe</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignUnsubscribeManager;
